// AuthLoginFlow — framework-light command class (contract 06 §8). Runs AuthService.login and
// returns a TYPED outcome (where to go); the composable does the navigation + notification.
// Errors surface as AuthError (mapped from ApiError). No layoutStore, no swal, no @/router.

import { AuthError } from '../models/auth.error'
import type { LoginCredentials, MfaChallenge } from '../models/auth.types'
import { authService, type AuthService } from '../service/AuthService'
import { useAuthStore, type AuthStore } from '../stores/authStore'

import { buildGoogleAuthRedirectUrl } from './googleAuthRedirect'
import { isForeignFrontendOrigin, redirectToMcpShellLogin } from './mcpShellRedirect'
import { getSafePostAuthRedirect, storePostLoginRedirect } from './postAuthRedirect'

import { LoginAccountContextFlow, type SessionAccessSection } from '@/features/account'
import { markLogin } from '@/features/auth/service/authAttribution'
import { useEnv } from '@/shared/config'
import { ApiError } from '@/shared/http'

const DEFAULT_TARGET = 'Summary'

/**
 * `/auth/login_v2/` still answers a rejected login with the pre-v4 flat body
 * (`{ error: "Invalid password" }`) instead of the `{ error: { code, message } }` envelope the
 * rest of module 06 relies on — so `ApiError.code` comes back null and `AuthError.from` falls
 * through to `unknown` (shown as the generic system-error copy, contract 06 §5's own escape
 * hatch, even though the login attempt was in fact rejected for bad credentials). This endpoint
 * has exactly one rejection reason (email/password are never validated field-by-field server
 * side), so a 400/401 with no machine code always means bad credentials here — classified by
 * response SHAPE and STATUS only, never by reading the English string, per the contract's
 * message-substring-branch ban.
 */
function normalizeLegacyLoginError(error: unknown): unknown {
  if (!ApiError.isApiError(error) || error.code) return error
  const payload = error.payload
  const isLegacyFlatBody =
    payload !== null &&
    typeof payload === 'object' &&
    typeof (payload as Record<string, unknown>).error === 'string'
  if (!isLegacyFlatBody || (error.status !== 400 && error.status !== 401)) return error
  return new ApiError({
    kind: error.kind,
    status: error.status,
    code: 'INVALID_CREDENTIALS',
    fields: error.fields,
    payload: error.payload,
    userVisible: error.userVisible,
    detail: error.detail,
    retryAfterMs: error.retryAfterMs,
  })
}

export type LoginOutcome =
  { next: 'mfa'; email: string; challenge: MfaChallenge } | { next: 'app'; target: string }

export interface AuthLoginDeps {
  service?: AuthService
  store?: AuthStore
  /** Full-page redirect (Google OAuth start). Injected for tests; defaults to window.location. */
  redirect?: (url: string) => void
  /** Hydrates loginV2's account list and chooses the bootstrap account before token publication. */
  prepareAccountContext?: (sessionAccess: SessionAccessSection) => void
}

export class AuthLoginFlow {
  private readonly service: AuthService
  private readonly store: AuthStore
  private readonly redirect: (url: string) => void
  private readonly prepareAccountContext: (sessionAccess: SessionAccessSection) => void

  constructor(deps: AuthLoginDeps = {}) {
    this.service = deps.service ?? authService
    this.store = deps.store ?? useAuthStore()
    this.redirect = deps.redirect ?? ((url) => window.location.assign(url))
    this.prepareAccountContext =
      deps.prepareAccountContext ??
      ((sessionAccess) => new LoginAccountContextFlow().execute(sessionAccess))
  }

  /** target precedence: the `next` query > persisted redirectTarget > Summary (§8). */
  async execute(creds: LoginCredentials, nextTarget?: string | null): Promise<LoginOutcome> {
    try {
      const result = await this.service.login(creds)
      if (result.type === 'mfa') {
        markLogin('Email')
        this.store.startMfaChallenge(result.challenge)
        // Persist the deep-link target so it survives the MFA leg (the /mfa route carries no
        // ?next); a `next` query wins over an already-remembered target (§8 precedence).
        if (nextTarget) this.store.rememberTarget(nextTarget)
        return { next: 'mfa', email: creds.email, challenge: result.challenge }
      }
      if (
        typeof result.frontendOrigin === 'string' &&
        isForeignFrontendOrigin(result.frontendOrigin) &&
        result.handoff
      ) {
        redirectToMcpShellLogin(result.frontendOrigin, result.handoff)
        return { next: 'app', target: DEFAULT_TARGET }
      }
      // `useBootstrap` watches this token synchronously. Seed the selected account first so its
      // first request is `/accounts/{selected}/bootstrap`, never the account-id=0 sentinel.
      this.prepareAccountContext(result.sessionAccess)
      markLogin('Email')
      this.store.setToken(result.token)
      return { next: 'app', target: nextTarget ?? this.store.redirectTarget ?? DEFAULT_TARGET }
    } catch (error) {
      throw AuthError.from(normalizeLegacyLoginError(error))
    }
  }

  /** `?verified` / `?show_reset_info` / `?change_success` login banners → an i18n key or null. */
  maybeShowVerifiedAlert(query: Record<string, unknown>): string | null {
    if ('verified' in query) return 'auth.login.verified'
    if ('show_reset_info' in query) return 'auth.login.resetInfo'
    if ('change_success' in query) return 'auth.login.changeSuccess'
    return null
  }

  /** Full-page redirect to the backend Google entry; the return is handled by ?handoff exchange. */
  startGoogleLoginRedirect(nextTarget?: string | null): void {
    markLogin('Google')
    const next = nextTarget ?? new URLSearchParams(window.location.search).get('next')
    const safeNext = getSafePostAuthRedirect(next)
    this.store.rememberTarget(safeNext ?? DEFAULT_TARGET)
    storePostLoginRedirect(safeNext)
    this.redirect(buildGoogleAuthRedirectUrl(useEnv().API_APP_URL))
  }
}
